import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { sendNotification } from '@/utils/sendNotification';

export interface ContactSubmission {
  id: string;
  category_id?: string;
  name: string;
  email: string;
  phone?: string;
  subject: string;
  message: string;
  status: 'new' | 'in_progress' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  assigned_to?: string;
  created_at: string;
  updated_at: string;
  resolved_at?: string;
  attachments?: any;
  attachments_json?: string;
  contact_categories?: {
    name: string;
    auto_response_template?: string;
  };
  profiles?: {
    name: string;
    email: string;
  };
}

export interface ContactResponse {
  id: string;
  submission_id: string;
  admin_id?: string;
  response_message: string;
  is_internal_note: boolean;
  created_at: string;
  profiles?: {
    name: string;
    email: string;
  };
}

export const useContactSubmissions = () => {
  const [submissions, setSubmissions] = useState<ContactSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    category: '',
    priority: '',
    assigned_to: ''
  });

  const fetchSubmissions = async () => {
    try {
      let query = supabase
        .from('contact_submissions')
        .select(`
          *,
          contact_categories(name, auto_response_template),
          profiles(name, email)
        `)
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters.status) {
        query = query.eq('status', filters.status as any);
      }
      if (filters.category) {
        query = query.eq('category_id', filters.category);
      }
      if (filters.priority) {
        query = query.eq('priority', filters.priority as any);
      }
      if (filters.assigned_to) {
        query = query.eq('assigned_to', filters.assigned_to);
      }

      const { data, error } = await query;

      if (error) throw error;
      setSubmissions(data || []);
    } catch (error) {
      console.error('Error fetching contact submissions:', error);
      toast.error('Failed to load contact submissions');
    } finally {
      setLoading(false);
    }
  };

  const updateSubmission = async (id: string, updates: Partial<ContactSubmission>) => {
    try {
      const { data, error } = await supabase
        .from('contact_submissions')
        .update({
          ...updates,
          resolved_at: updates.status === 'resolved' ? new Date().toISOString() : undefined
        })
        .eq('id', id)
        .select(`
          *,
          contact_categories(name, auto_response_template),
          profiles(name, email)
        `)
        .single();

      if (error) throw error;
      
      setSubmissions(prev => 
        prev.map(sub => sub.id === id ? { ...sub, ...data } : sub)
      );
      toast.success('Submission updated successfully');
      return data;
    } catch (error) {
      console.error('Error updating contact submission:', error);
      toast.error('Failed to update submission');
      throw error;
    }
  };

  const getResponses = async (submissionId: string): Promise<ContactResponse[]> => {
    try {
      const { data, error } = await supabase
        .from('contact_responses')
        .select(`
          id,
          submission_id,
          admin_id,
          response_message,
          is_internal_note,
          created_at,
          profiles!contact_responses_admin_id_fkey(name, email)
        `)
        .eq('submission_id', submissionId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching responses:', error);
      toast.error('Failed to load responses');
      return [];
    }
  };

  const addResponse = async (submissionId: string, responseData: {
    response_message: string;
    is_internal_note: boolean;
  }) => {
    try {
      const { data, error } = await supabase
        .from('contact_responses')
        .insert([{
          submission_id: submissionId,
          ...responseData
        }])
        .select(`
          id,
          submission_id,
          admin_id,
          response_message,
          is_internal_note,
          created_at,
          profiles!contact_responses_admin_id_fkey(name, email)
        `)
        .single();

      if (error) throw error;
      
      // Send email notification for non-internal responses
      if (!responseData.is_internal_note) {
        try {
          // Get submission details for email context
          const { data: submission } = await supabase
            .from('contact_submissions')
            .select('name, email, subject')
            .eq('id', submissionId)
            .single();
            
          if (submission) {
            await sendNotification('contactStatusUpdate', submission.email, {
              name: submission.name,
              subject: submission.subject,
              response_message: responseData.response_message,
              status: 'response_added'
            });
          }
        } catch (emailError) {
          console.error('Failed to send status update email:', emailError);
          // Don't fail the response if email fails
        }
      }
      
      toast.success(responseData.is_internal_note ? 'Internal note added' : 'Response sent');
      return data;
    } catch (error) {
      console.error('Error adding response:', error);
      toast.error('Failed to add response');
      throw error;
    }
  };

  useEffect(() => {
    fetchSubmissions();
  }, [filters]);

  return {
    submissions,
    loading,
    filters,
    setFilters,
    updateSubmission,
    getResponses,
    addResponse,
    refetch: fetchSubmissions
  };
};